/**
 * In-run progression HUD: a thin XP bar under the action bar (level + XP, live and provisional),
 * the mid-run "Level up!" banner, and the queue that times kill-reward pops with the killing
 * blow's damage number. The purse (gold / Shards ticking up) and the loot feed are HUD widgets
 * (hud/panels) fed through `progressFeed`: `begin` with the purse, `kill` when a pop is released.
 *
 * Fed by the session through Hud: `start(tally)` once, `apply(tick)` per tick that paid rewards,
 * `ingestText(events)` with the combat-text stream and `update(now)` every frame.
 */
import type { SimEvent, UnitId } from '@keystone/sim';
import type { RewardPop } from '../hud/floatingText';
import { el, pct, setClass, setStyle, setText } from '../hud/dom';
import type { HeroProgressView, InRunStart, TallyTick } from './inrunTally';
import { progressFeed } from '../hud/panels/progressFeed';
import './inrun.css';

/** A reward pop waits for its killing blow's text (held while a projectile flies) up to this long. */
export const REWARD_HOLD_MS = 650;
/** Counters ease to their new value over this long. */
const COUNT_MS = 650;
const BANNER_MS = 2800;

interface PendingPop {
  pop: RewardPop & { npcId: string };
  at: number;
  ready: boolean;
}

/** A number that eases toward its target (ticking counter). */
class Counter {
  shown: number;
  private from: number;
  private target: number;
  private t0 = 0;
  constructor(v: number) {
    this.shown = this.from = this.target = v;
  }
  set(v: number, now: number): void {
    if (v === this.target) return;
    this.from = this.shown;
    this.target = v;
    this.t0 = now;
  }
  step(now: number): number {
    const k = Math.min(1, Math.max(0, (now - this.t0) / COUNT_MS));
    this.shown = this.from + (this.target - this.from) * (1 - (1 - k) ** 3);
    return Math.round(this.shown);
  }
}

export interface InRunHudParts {
  /** Where the XP bar goes (the action-bar dock; the bar spans its width). Null: no legacy XP bar (the skill cluster draws its own from `inRunView`). */
  dock: HTMLElement | null;
  /** Unused (the purse moved to the HUD's currencies row); kept for callers. */
  keystone?: HTMLElement;
  /** HUD root (banner). */
  root: HTMLElement;
  /** Spawns one kill-reward pop (floating text). */
  pop(p: RewardPop, now: number): void;
}

/**
 * Live in-run progression for HUD widgets that cannot take constructor arguments (the skill
 * cluster's crest level / gold / XP hairline, the currencies row). InRunHud writes it; readers
 * poll it per frame (plain fields, no allocation). `version` bumps whenever level / XP change.
 */
export interface InRunView {
  active: boolean;
  classId: string | null;
  heroName: string;
  level: number;
  /** XP into the level, and needed for the next (0 at max level). */
  xp: number;
  next: number;
  maxed: boolean;
  /** XP into the current level when the run started (0 after a level-up): the "gain" segment starts here. */
  startXp: number;
  /** XP earned by kills this run. */
  runXp: number;
  /** Gold as shown (eases toward the new total after a kill reward), and the total it eases to. */
  gold: number;
  goldTarget: number;
  shards: number;
  version: number;
}

export const inRunView: InRunView = {
  active: false,
  classId: null,
  heroName: '',
  level: 0,
  xp: 0,
  next: 0,
  maxed: false,
  startXp: 0,
  runXp: 0,
  gold: 0,
  goldTarget: 0,
  shards: 0,
  version: 0,
};

export class InRunHud {
  private readonly xpRoot: HTMLElement;
  private readonly xpLevel: HTMLElement;
  private readonly xpText: HTMLElement;
  private readonly xpFill: HTMLElement;
  private readonly xpGain: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly bannerTitle: HTMLElement;
  private readonly bannerSub: HTMLElement;
  private start: InRunStart | null = null;
  private hero: HeroProgressView | null = null;
  private runXp = 0;
  private runGold = 0;
  private gold = new Counter(0);
  private bannerUntil = 0;
  private pending: PendingPop[] = [];
  private pendingIds = new Set<UnitId>();
  private dirty = true;

  constructor(private readonly parts: InRunHudParts) {
    this.xpRoot = el('div', 'ir-xp hidden', parts.dock ?? undefined);
    this.xpRoot.title = 'Hero XP from kills so far. Completion and timer bonuses are added when the run ends.';
    const head = el('div', 'ir-xp-head', this.xpRoot);
    this.xpLevel = el('span', 'ir-xp-level', head);
    this.xpText = el('span', 'ir-xp-text', head);
    const track = el('div', 'ir-xp-track', this.xpRoot);
    this.xpFill = el('div', 'ir-xp-fill', track);
    this.xpGain = el('div', 'ir-xp-gain', track);
    for (let i = 1; i < 10; i++) el('i', 'ir-xp-tick', track).style.left = `${i * 10}%`;

    this.banner = el('div', 'ir-levelup', parts.root);
    el('div', 'ir-levelup-burst', this.banner);
    this.bannerTitle = el('div', 'ir-levelup-title', this.banner, 'Level up!');
    this.bannerSub = el('div', 'ir-levelup-sub', this.banner);
  }

  /** Initial state (null hides the progression widgets, e.g. a run without a profile). */
  begin(start: InRunStart | null, hero: HeroProgressView | null): void {
    this.start = start;
    this.hero = hero;
    const v = inRunView;
    v.active = !!start;
    v.classId = start?.classId ?? null;
    v.heroName = start?.heroName ?? '';
    v.level = hero?.level ?? start?.level ?? 0;
    v.xp = hero?.xp ?? start?.xp ?? 0;
    v.next = hero?.next ?? 0;
    v.maxed = hero?.maxed ?? false;
    v.startXp = start?.xp ?? 0;
    v.runXp = 0;
    v.gold = v.goldTarget = start?.gold ?? 0;
    v.shards = start?.shards ?? 0;
    v.version++;
    this.runXp = 0;
    this.runGold = 0;
    this.gold = new Counter(start?.gold ?? 0);
    setClass(this.xpRoot, 'hidden', !start);
    progressFeed.begin(start ? { gold: start.gold, shards: start.shards, heroLevel: start.level } : null);
    this.dirty = true;
  }

  /** One tick's new rewards: queue the pops, move the bars, maybe show the banner. */
  apply(tick: TallyTick, now: number): void {
    for (const e of tick.entries) {
      this.pending.push({ pop: e, at: now, ready: false });
      this.pendingIds.add(e.unitId);
    }
    this.runXp += tick.xp;
    this.runGold += tick.gold;
    this.hero = tick.hero;
    if (this.start) {
      const v = inRunView;
      v.level = tick.hero.level;
      v.xp = tick.hero.xp;
      v.next = tick.hero.next;
      v.maxed = tick.hero.maxed;
      v.startXp = tick.hero.level === this.start.level ? this.start.xp : 0;
      v.runXp = this.runXp;
      v.goldTarget = this.start.gold + this.runGold;
      v.version++;
    }
    if (this.start) this.gold.set(this.start.gold + this.runGold, now);
    if (tick.levelsGained > 0 && this.start) this.levelUp(tick.hero, tick.levelsGained, now);
    this.dirty = true;
  }

  /** Combat-text events of this frame: a reward pops with (or after) its killing blow's number. */
  ingestText(events: readonly SimEvent[]): void {
    if (!this.pendingIds.size) return;
    for (const e of events) {
      if (e.type !== 'damage' || !this.pendingIds.has(e.targetID)) continue;
      for (const p of this.pending) if (p.pop.unitId === e.targetID) p.ready = true;
    }
  }

  update(now: number): void {
    if (this.pending.length) {
      const keep: PendingPop[] = [];
      for (const p of this.pending) {
        if (p.ready || now - p.at >= REWARD_HOLD_MS) {
          this.parts.pop(p.pop, now);
          progressFeed.kill(p.pop, now);
          this.pendingIds.delete(p.pop.unitId);
        } else keep.push(p);
      }
      this.pending = keep;
    }
    if (this.start) inRunView.gold = this.gold.step(now);
    setClass(this.banner, 'show', now < this.bannerUntil);
    if (!this.dirty || !this.hero || !this.start) return;
    this.dirty = false;
    const h = this.hero;
    setText(this.xpLevel, `Lv ${h.level}${this.start.heroName ? ` · ${this.start.heroName}` : ''}`);
    if (h.maxed) {
      setText(this.xpText, 'Max level');
      setStyle(this.xpFill, 'width', '100%');
      setStyle(this.xpGain, 'width', '0%');
      return;
    }
    setText(this.xpText, `${h.xp} / ${h.next} XP${this.runXp > 0 ? `  ·  +${this.runXp} this run` : ''}`);
    // The violet "gain" segment is this run's XP within the current level (all of it after a level-up).
    const startXp = h.level === this.start.level ? this.start.xp : 0;
    setStyle(this.xpFill, 'width', pct(startXp / h.next));
    setStyle(this.xpGain, 'left', pct(startXp / h.next));
    setStyle(this.xpGain, 'width', pct((h.xp - startXp) / h.next));
  }

  private levelUp(hero: HeroProgressView, gained: number, now: number): void {
    const pts = gained === 1 ? '+1 talent point' : `+${gained} talent points`;
    setText(this.bannerTitle, gained > 1 ? `Level up! ×${gained}` : 'Level up!');
    setText(this.bannerSub, `Level ${hero.level}  ·  ${pts} to spend in the hub`);
    this.banner.classList.remove('show', 'play');
    void this.banner.offsetWidth; // restart the burst
    this.banner.classList.add('play', 'show');
    this.bannerUntil = now + BANNER_MS;
    progressFeed.levelUp(hero.level, now);
    this.xpRoot.classList.remove('leveled');
    void this.xpRoot.offsetWidth;
    this.xpRoot.classList.add('leveled');
  }

  dispose(): void {
    this.pending = [];
    this.pendingIds.clear();
    if (inRunView.active) {
      inRunView.active = false;
      inRunView.version++;
    }
    this.xpRoot.remove();
    progressFeed.end();
    this.banner.remove();
  }
}
